import {
    PDFDocument, StandardFonts, clip, endPath, popGraphicsState, pushGraphicsState, rectangle, rgb,
} from 'pdf-lib';
import { DrawKind } from './engine/fr3DrawOp';
import { FRAME_BOTTOM, FRAME_LEFT, FRAME_RIGHT, FRAME_TOP, HAlign, VAlign } from './engine/fr3Doc';

/**
 * Paints the engine's draw ops into a PDF with pdf-lib - the browser
 * counterpart of the tablet's Fr3PdfRenderer and the harness painter.
 *
 * Emitter rules shared with the Java side (MyFasManMobile
 * tools/fr3harness/golden/README.md): text wrapped with real font metrics
 * inside the box minus an inset of 1.5 pt left/right and 0.75 pt top, clipped
 * to its box; images scaled to fit keeping the aspect ratio (centered when
 * `imageCenter`); frame sides stroked at width * 0.75 pt.
 *
 * The standard PDF fonts stand in for the Windows ones: Helvetica for Arial
 * (identical advance widths), Times for Times New Roman, Courier for Courier
 * New. They only cover WinAnsi; other characters print as '?'.
 */

/** FastReport insets memo text slightly inside its box. */
const GAP_X = 2 * 0.75;
const GAP_Y = 1 * 0.75;

/** Line metrics of the Windows fonts the templates name, as a fraction of the size. */
const METRICS = {
    sans: { ascent: 0.905, height: 1.149 },
    serif: { ascent: 0.891, height: 1.149 },
    mono: { ascent: 0.833, height: 1.133 },
};

const familyOf = (fontName) => {
    const n = String(fontName || '').toLowerCase();
    if (n.includes('courier') || n.includes('mono') || n.includes('consol')) return 'mono';
    if (n.includes('times') || n.includes('serif') || n.includes('georgia') || n.includes('garamond')) return 'serif';
    return 'sans';
};

const STANDARD = {
    sans: [StandardFonts.Helvetica, StandardFonts.HelveticaBold, StandardFonts.HelveticaOblique, StandardFonts.HelveticaBoldOblique],
    serif: [StandardFonts.TimesRoman, StandardFonts.TimesRomanBold, StandardFonts.TimesRomanItalic, StandardFonts.TimesRomanBoldItalic],
    mono: [StandardFonts.Courier, StandardFonts.CourierBold, StandardFonts.CourierOblique, StandardFonts.CourierBoldOblique],
};

const color = (rgbInt) => rgb(((rgbInt >> 16) & 0xFF) / 255, ((rgbInt >> 8) & 0xFF) / 255, (rgbInt & 0xFF) / 255);

const isPng = (b) => b.length > 8 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4E && b[3] === 0x47;
const isJpeg = (b) => b.length > 3 && b[0] === 0xFF && b[1] === 0xD8 && b[2] === 0xFF;

/** BMP / GIF (and anything else the browser decodes) -> PNG bytes, via a canvas. null when unavailable. */
const toPng = async (bytes) => {
    try {
        if (typeof createImageBitmap !== 'function' || typeof Blob === 'undefined') return null;
        const bitmap = await createImageBitmap(new Blob([bytes]));
        let blob;
        if (typeof OffscreenCanvas !== 'undefined') {
            const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
            canvas.getContext('2d').drawImage(bitmap, 0, 0);
            blob = await canvas.convertToBlob({ type: 'image/png' });
        } else if (typeof document !== 'undefined') {
            const canvas = document.createElement('canvas');
            canvas.width = bitmap.width;
            canvas.height = bitmap.height;
            canvas.getContext('2d').drawImage(bitmap, 0, 0);
            blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
        }
        if (!blob) return null;
        return new Uint8Array(await blob.arrayBuffer());
    } catch (e) {
        return null;
    }
};

/**
 * @param pages  the engine's RenderedPages: [{ widthPoints, heightPoints, ops }]
 * @param opts   { title }
 * @returns Promise<{ pdf: Uint8Array, warnings: string[] }>
 */
export const emitPdf = async (pages, opts = {}) => {
    const pdf = await PDFDocument.create();
    if (opts.title) pdf.setTitle(String(opts.title));
    pdf.setProducer('MySoftTouch fr3 preview');
    const warnings = [];

    const fonts = {};
    const charsets = {};
    const font = async (family, bold, italic) => {
        const idx = (bold ? 1 : 0) + (italic ? 2 : 0);
        const key = family + idx;
        if (!fonts[key]) {
            fonts[key] = await pdf.embedFont(STANDARD[family][idx]);
            charsets[key] = new Set(fonts[key].getCharacterSet());
        }
        return { font: fonts[key], charset: charsets[key] };
    };

    // Images are embedded once per distinct byte array (the logo repeats on every page).
    const images = new Map();
    const image = async (bytes) => {
        if (images.has(bytes)) return images.get(bytes);
        let embedded = null;
        try {
            if (isPng(bytes)) embedded = await pdf.embedPng(bytes);
            else if (isJpeg(bytes)) embedded = await pdf.embedJpg(bytes);
            else {
                const png = await toPng(bytes);
                if (png) embedded = await pdf.embedPng(png);
                else warnings.push('an image (BMP/GIF) could not be converted for the preview');
            }
        } catch (e) {
            warnings.push('an image could not be embedded: ' + (e && e.message ? e.message : e));
        }
        images.set(bytes, embedded);
        return embedded;
    };

    for (const page of pages) {
        const pdfPage = pdf.addPage([page.widthPoints, page.heightPoints]);
        const H = page.heightPoints;
        for (const op of page.ops) {
            switch (op.kind) {
                case DrawKind.FILL:
                    if (op.w > 0 && op.h > 0) {
                        pdfPage.drawRectangle({
                            x: op.x, y: H - op.y - op.h, width: op.w, height: op.h, color: color(op.fillColor),
                        });
                    }
                    break;
                case DrawKind.FRAME:
                    drawFrame(pdfPage, op, H);
                    break;
                case DrawKind.IMAGE:
                    if (op.image) {
                        const img = await image(op.image);
                        if (img) drawImage(pdfPage, op, img, H);
                    }
                    break;
                case DrawKind.TEXT: {
                    const family = familyOf(op.fontName);
                    const f = await font(family, op.bold, op.italic);
                    drawText(pdfPage, op, f.font, f.charset, METRICS[family], H);
                    break;
                }
                default:
                    break;
            }
        }
    }
    const bytes = await pdf.save();
    return { pdf: bytes, warnings };
};

const drawFrame = (page, op, H) => {
    const thickness = Math.max(0.4, op.frameWidth * 0.75);
    const c = color(op.frameColor);
    const x1 = op.x;
    const x2 = op.x + op.w;
    const y1 = H - op.y;
    const y2 = H - (op.y + op.h);
    const line = (sx, sy, ex, ey) => page.drawLine({ start: { x: sx, y: sy }, end: { x: ex, y: ey }, thickness, color: c });
    if (op.frameSides & FRAME_LEFT) line(x1, y1, x1, y2);
    if (op.frameSides & FRAME_RIGHT) line(x2, y1, x2, y2);
    if (op.frameSides & FRAME_TOP) line(x1, y1, x2, y1);
    if (op.frameSides & FRAME_BOTTOM) line(x1, y2, x2, y2);
};

const drawImage = (page, op, img, H) => {
    if (!(img.width > 0 && img.height > 0) || op.w <= 0 || op.h <= 0) return;
    const s = Math.min(op.w / img.width, op.h / img.height);
    const dw = img.width * s;
    const dh = img.height * s;
    const dx = op.imageCenter ? op.x + (op.w - dw) / 2 : op.x;
    const dy = op.imageCenter ? op.y + (op.h - dh) / 2 : op.y;
    page.drawImage(img, { x: dx, y: H - dy - dh, width: dw, height: dh });
};

const SUBSTITUTES = {
    ' ': ' ', ' ': ' ', '​': '', '\t': ' ', '−': '-', '‐': '-', '‑': '-',
};

/**
 * Characters the standard font cannot encode become '?' (pdf-lib would
 * throw). Line breaks pass through: layoutLines() splits on them.
 */
const encodable = (text, charset) => {
    let out = '';
    for (const ch of text) {
        if (ch === '\r' || ch === '\n') {
            out += ch;
            continue;
        }
        const sub = Object.prototype.hasOwnProperty.call(SUBSTITUTES, ch) ? SUBSTITUTES[ch] : ch;
        for (const c of sub) out += charset.has(c.codePointAt(0)) ? c : '?';
    }
    return out;
};

/** The harness's line layout: hard breaks, then greedy word wrap within `avail`. */
const layoutLines = (op, text, width) => {
    const out = [];
    const avail = op.w - 2 * GAP_X;
    for (const hard of text.split(/\r\n|\n|\r/)) {
        if (!op.wordWrap || width(hard) <= avail) {
            out.push(hard);
            continue;
        }
        let cur = '';
        for (const word of hard.split(' ')) {
            const probe = cur.length === 0 ? word : cur + ' ' + word;
            if (width(probe) <= avail || cur.length === 0) {
                cur = probe;
            } else {
                out.push(cur);
                cur = word;
            }
        }
        out.push(cur);
    }
    return out;
};

const drawText = (page, op, font, charset, metrics, H) => {
    const size = op.fontSize;
    if (!(size > 0) || op.w <= 0 || op.h <= 0) return;
    const text = encodable(op.text, charset);
    const width = (s) => font.widthOfTextAtSize(s, size);
    const lines = layoutLines(op, text, width);
    const lineHeight = metrics.height * size;
    const ascent = metrics.ascent * size;
    const totalH = lineHeight * lines.length;

    let y;
    switch (op.vAlign) {
        case VAlign.CENTER: y = op.y + (op.h - totalH) / 2 + ascent; break;
        case VAlign.BOTTOM: y = op.y + op.h - totalH + ascent - GAP_Y; break;
        default: y = op.y + GAP_Y + ascent; break;
    }

    const c = color(op.textColor);
    // Clip to the object box: FastReport never paints outside a memo.
    page.pushOperators(pushGraphicsState(), rectangle(op.x, H - op.y - op.h, op.w, op.h), clip(), endPath());
    for (const line of lines) {
        if (line !== '') {
            const lw = width(line);
            let x;
            switch (op.hAlign) {
                case HAlign.RIGHT: x = op.x + op.w - GAP_X - lw; break;
                case HAlign.CENTER: x = op.x + (op.w - lw) / 2; break;
                default: x = op.x + GAP_X; break;
            }
            page.drawText(line, { x, y: H - y, size, font, color: c });
            if (op.underline) {
                const uy = H - y - size * 0.11;
                page.drawLine({ start: { x, y: uy }, end: { x: x + lw, y: uy }, thickness: Math.max(0.3, size * 0.05), color: c });
            }
        }
        y += lineHeight;
    }
    page.pushOperators(popGraphicsState());
};

export default emitPdf;
